import { useMemo } from 'react';
import type { AnalysisResult } from '@/core/analysis/types';
import { upcomingChange } from '@/core/analysis/segments';
import { pianoKeys, TUNINGS, voicingsFor, type Instrument } from '@/core/instruments/shapes';
import { chordName, type DisplayOptions } from '@/core/theory/display';
import { useTimeIndex } from '@/lib/useTimeIndex';
import { useSettingsStore } from '@/state/settingsStore';
import { ChevronLeftIcon, ChevronRightIcon } from '@/ui/icons';
import {
  fretsFor,
  layoutFor,
  shapeRootOf,
  STRING_SPACING as SPACING,
  voicingKey,
} from './chordShape';
import { FretDiagram } from './FretDiagram';
import { PianoDiagram } from './PianoDiagram';
import { Switch } from '@/ui/Switch';
import { InstrumentSelect } from './InstrumentSelect';
import styles from './ShapesPanel.module.css';
import { useVoicingPrefs } from './useVoicingPrefs';

interface ShapesPanelProps {
  result: AnalysisResult;
  options: DisplayOptions;
  instrument: Instrument;
  onInstrument: (instrument: Instrument) => void;
  leftHanded: boolean;
  onLeftHanded: (on: boolean) => void;
  visualOffsetMs: number;
  /** Flash the next chord this many beats ahead (F4.7); 0 = off. */
  warnBeats: number;
}

/** Current and next chord shapes (F4.4, F4.8, F4.10; design: Chord shapes card). */
export function ShapesPanel({
  result,
  options,
  instrument,
  onInstrument,
  leftHanded,
  onLeftHanded,
  visualOffsetMs,
  warnBeats,
}: ShapesPanelProps) {
  const starts = useMemo(() => result.segments.map((s) => s.start), [result]);
  const index = useTimeIndex(starts, visualOffsetMs);
  const current = index >= 0 ? result.segments[index] : undefined;
  const next = result.segments[index + 1];
  const nowName = current ? chordName(current.chord, options) : '–';
  const nextName = next ? chordName(next.chord, options) : '—';

  const fretted = instrument === 'piano' ? null : instrument;
  const beat = useTimeIndex(result.beatGrid.beats, visualOffsetMs);
  const warn = next !== undefined && upcomingChange(result.segments, beat, warnBeats) === next;
  const prefs = useVoicingPrefs();
  const update = useSettingsStore((s) => s.update);

  // Voicing picker (F4.9): which fingering of the current chord, remembered per chord shape.
  const chord = current?.chord ?? null;
  const picker = (() => {
    if (!fretted || !chord) return null;
    const root = shapeRootOf(chord, options);
    const all = voicingsFor(fretted, root, chord.quality);
    if (all.length < 2) return null;
    const shown = fretsFor(chord, fretted, options, prefs).join(',');
    const index = Math.max(
      0,
      all.findIndex((v) => v.join(',') === shown),
    );
    const choose = (i: number) => {
      update({
        voicingChoices: {
          ...prefs.choices,
          [voicingKey(fretted, root, chord)]: (i + all.length) % all.length,
        },
      });
    };
    return { index, count: all.length, choose };
  })();

  return (
    <section aria-label="Chord shapes" className={styles.card}>
      <InstrumentSelect label="Instrument" value={instrument} onChange={onInstrument} stretch />

      <div className={styles.shapes}>
        <div className={styles.now}>
          <span className={styles.eyebrow}>Now</span>
          <span className={styles.nowName} aria-live="polite" data-testid="now-chord">
            {nowName}
          </span>
          {fretted ? (
            <>
              <FretDiagram
                layout={layoutFor(chord, fretted, options, leftHanded, prefs)}
                strings={TUNINGS[fretted].length}
                spacing={SPACING[fretted]}
              />
              {picker && (
                <div className={styles.picker} role="group" aria-label="Chord shape">
                  <button
                    type="button"
                    className={styles.pickerButton}
                    aria-label="Previous shape"
                    onClick={() => {
                      picker.choose(picker.index - 1);
                    }}
                  >
                    <ChevronLeftIcon size={16} />
                  </button>
                  <span className={styles.pickerLabel}>
                    Shape {picker.index + 1} of {picker.count}
                  </span>
                  <button
                    type="button"
                    className={styles.pickerButton}
                    aria-label="Next shape"
                    onClick={() => {
                      picker.choose(picker.index + 1);
                    }}
                  >
                    <ChevronRightIcon size={16} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <PianoDiagram keys={pianoKeys(current?.chord ?? null, options.transpose)} />
          )}
        </div>

        <div className={styles.next} data-warn={warn || undefined}>
          <span className={styles.eyebrow}>Next</span>
          <span className={styles.nextName}>{nextName}</span>
          {fretted && (
            <div className={styles.nextDiagram}>
              <div className={styles.scaled}>
                <FretDiagram
                  layout={layoutFor(next?.chord ?? null, fretted, options, leftHanded, prefs)}
                  strings={TUNINGS[fretted].length}
                  spacing={SPACING[fretted]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {fretted && (
          <div className={styles.switchRow}>
            <span id="left-handed-label" className={styles.switchLabel}>
              Left-handed
            </span>
            <Switch labelledBy="left-handed-label" checked={leftHanded} onChange={onLeftHanded} />
          </div>
        )}
        {!fretted && (
          <span className={styles.hint}>
            Piano shows the notes as they sound; the capo doesn&apos;t apply.
          </span>
        )}
        {instrument === 'bass' && (
          <span className={styles.hint}>Bass shows the root, with the fifth where it fits.</span>
        )}
      </div>
    </section>
  );
}
